import type { ShapeData } from '@/lib/shapes/types/shape-data'
import {
  ROUNDING_MAX,
  ROUNDING_MIN,
  ROUNDING_RANGE_ERROR,
  isRoundingEligibleShape,
  parseRoundingPercent,
} from '@/lib/object-effects/rounding'
import { useGeometryEffectControl } from '../hooks/use-geometry-effect-control'
import type { EffectControlProps } from '../types/effect-control-props'
import type { GeometryEffect } from '../types/geometry-effect'
import { EffectSliderField } from './effect-slider-field'

const ROUNDING_PRESETS = [
  { label: '0%', value: 0 },
  { label: '25%', value: 25 },
  { label: '50%', value: 50 },
  { label: '75%', value: 75 },
  { label: '100%', value: 100 },
] as const

/** Clamp + round a raw `roundedCorner` value. */
function normRoundedValue(raw: number | undefined): number {
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return 0
  return Math.max(ROUNDING_MIN, Math.min(ROUNDING_MAX, Math.round(raw)))
}

function normRounded(s: ShapeData): number {
  return normRoundedValue(s.roundedCorner)
}

function readLiveRoundedValue(id: string, liveShapes: readonly ShapeData[]): number {
  return normRoundedValue(liveShapes.find((s) => s.id === id)?.roundedCorner)
}

/** Same "shared value, or blank when they disagree" rule as `resolveEffectSelection`. */
function mergedCommonAndSlider(
  ids: readonly string[],
  liveShapes: readonly ShapeData[]
): { commonValue: number | null; sliderValue: number } {
  if (ids.length === 0) return { commonValue: 0, sliderValue: 0 }
  const values = ids.map((id) => readLiveRoundedValue(id, liveShapes))
  const first = values[0]
  const allSame = values.every((v) => v === first)

  return { commonValue: allSame ? first : null, sliderValue: first }
}

function parseRoundingInput(raw: string): number | null {
  const p = parseRoundingPercent(raw)
  const v = p.ok ? Math.round(p.value) : NaN

  if (!p.ok || v < ROUNDING_MIN || v > ROUNDING_MAX) return null
  return v
}

const ROUNDING_EFFECT: GeometryEffect = {
  restValue: 0,
  min: ROUNDING_MIN,
  max: ROUNDING_MAX,
  presets: ROUNDING_PRESETS,
  rangeError: ROUNDING_RANGE_ERROR,
  isEligible: isRoundingEligibleShape,
  readValue: normRounded,
  normalize: normRoundedValue,
  parseInput: parseRoundingInput,
  liveUpdate: (_cur, v) => ({ roundedCorner: v }),
  historyItem: (_sh, start, v) => ({
    before: { roundedCorner: start },
    after: { roundedCorner: v },
  }),
  // Session start values come from the shapes of the last render, not a live ShapeManager read.
  startSource: 'rendered',
  // Values are read by id from the given list (a shape missing from it reads as 0).
  readCommonValue: (eligible, source) =>
    mergedCommonAndSlider(
      eligible.map((s) => s.id),
      source
    ),
}

/**
 * Rounding 0–100% (Object Effects) for rectangle/square shapes (see
 * `isRoundingEligibleShapeType` in `lib/object-effects/rounding.ts`). Field behavior and the
 * multi-selection rules are shared with Sides / Inset — see `use-geometry-effect-control`.
 */
export function EffectsPanelRounding(props: EffectControlProps) {
  const field = useGeometryEffectControl(ROUNDING_EFFECT, props)

  return (
    <EffectSliderField
      {...field}
      label="Rounding"
      ariaLabel="Rounding percent"
      errorId="rounding-input-error"
      suffix="%"
    />
  )
}
